// .pen: G6gH8/O2vVK (Selfie · Ritual · Confirmação · default|datas) + ZblvR (ds/selfie/confirm-receipt)
import { useState } from 'react';
import { CaretDown, CaretUp } from '@phosphor-icons/react';

import { Input } from '../../ui/Input';
import pt from '../../../locales/pt';
import { formatEUR } from '../../../utils/money';

/**
 * Resumo antes da revelação + datas de ciclo opcionais (fora do caminho crítico).
 * .pen: "Selfie · Ritual · Confirmação · default|datas" › copy + confirm-receipt (ZblvR)
 * title/subtitle/adjustDatesLabel: override para o modo edição
 * (.pen: OVQ8Q "Antes de guardar…" / "Ajustar data da selfie")
 */
export function SelfieConfirmStep({
  assetsSum,
  debtsSum,
  netWorth,
  showDateFields = false,
  cycleStart,
  onCycleStartChange,
  minCycleStart,
  snapshotDate,
  onSnapshotDateChange,
  maxSnapshotDate,
  title,
  subtitle,
  adjustDatesLabel,
}) {
  const [datesOpen, setDatesOpen] = useState(false);
  const copy = pt.snapshots.ritual.confirm;
  // Edição não tem data de início de ciclo editável — só mostra o campo se
  // houver handler (evita um campo de data sem efeito, .pen só modela 1 campo aqui).
  const showCycleStartField = typeof onCycleStartChange === 'function';

  return (
    <div className="flex flex-col gap-space-5">
      <div className="flex flex-col gap-space-2">
        <h1 className="font-display text-type-title-page font-semibold leading-[1.25] tracking-tight text-foreground">
          {title || copy.title}
        </h1>
        <p className="text-type-body leading-[1.55] text-muted-foreground">{subtitle || copy.subtitle}</p>
      </div>

      <div
        data-ds-id="ds/selfie/confirm-receipt"
        className="overflow-hidden rounded-ds-lg border border-border"
      >
        <div className="flex items-center justify-between gap-space-3 border-b border-border bg-card px-space-4 py-space-3">
          <span className="text-type-body-sm text-muted-foreground">{copy.assetsLabel}</span>
          <span className="font-display text-type-title-card font-bold tabular-nums text-sage-on-tint">
            {formatEUR(assetsSum)}
          </span>
        </div>
        <div className="flex items-center justify-between gap-space-3 border-b border-border bg-card px-space-4 py-space-3">
          <span className="text-type-body-sm text-muted-foreground">{copy.debtsLabel}</span>
          <span className="font-display text-type-title-card font-bold tabular-nums text-warning-fg">
            −{formatEUR(debtsSum)}
          </span>
        </div>
        <div className="flex items-center justify-between gap-space-3 bg-sage-tint px-space-4 py-space-4">
          <span className="type-body font-semibold text-card-foreground">{copy.netLabel}</span>
          <span className="font-display text-type-display-metric font-bold tabular-nums text-card-foreground">
            {formatEUR(netWorth)}
          </span>
        </div>
      </div>

      {showDateFields ? (
        <div>
          <button
            type="button"
            className="inline-flex items-center gap-space-1 text-type-body-sm font-semibold text-sage-on-tint"
            onClick={() => setDatesOpen((open) => !open)}
          >
            {datesOpen ? (
              <CaretUp className="size-3.5" weight="bold" aria-hidden />
            ) : (
              <CaretDown className="size-3.5" weight="bold" aria-hidden />
            )}
            {adjustDatesLabel || pt.snapshots.ritual.moment.adjustDates}
          </button>
          {datesOpen ? (
            <div className="mt-space-3 flex flex-col gap-space-4 rounded-ds-lg border border-border bg-card p-space-4">
              {showCycleStartField ? (
                <div className="flex flex-col gap-1">
                  <label
                    className="text-xs font-medium text-muted-foreground"
                    htmlFor="ritual-cycle-start"
                  >
                    {pt.snapshots.new.cycleStartDateLabel}
                  </label>
                  <Input
                    id="ritual-cycle-start"
                    type="date"
                    value={cycleStart ?? ''}
                    min={minCycleStart ?? undefined}
                    max={snapshotDate || maxSnapshotDate || undefined}
                    onChange={(event) => onCycleStartChange?.(event.target.value)}
                  />
                </div>
              ) : null}
              <div className="flex flex-col gap-1">
                <label
                  className="text-xs font-medium text-muted-foreground"
                  htmlFor="ritual-snapshot-date"
                >
                  {copy.snapshotDateLabel}
                </label>
                <Input
                  id="ritual-snapshot-date"
                  type="date"
                  value={snapshotDate ?? ''}
                  min={cycleStart || minCycleStart || undefined}
                  max={maxSnapshotDate ?? undefined}
                  onChange={(event) => onSnapshotDateChange?.(event.target.value)}
                />
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default SelfieConfirmStep;
